import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, Copy, Loader2, Link2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../context/AdminContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminRedirects = () => {
  const { toast } = useToast();
  const [redirects, setRedirects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ slug: '', target_url: '', is_active: true });

  const loadRedirects = useCallback(async () => {
    try {
      const resp = await adminApi.get('/redirects');
      setRedirects(resp.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load redirects', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadRedirects(); }, [loadRedirects]);

  const resetForm = () => {
    setForm({ slug: '', target_url: '', is_active: true });
    setEditing(null);
  };

  const handleSave = async () => {
    if (!form.slug || !form.target_url) {
      toast({ title: 'Error', description: 'Slug and target URL are required', variant: 'destructive' });
      return;
    }
    if (!/^[a-z0-9-]+$/.test(form.slug)) {
      toast({ title: 'Error', description: 'Slug can only contain lowercase letters, numbers, and hyphens', variant: 'destructive' });
      return;
    }
    try {
      const target = new URL(form.target_url);
      if (!['http:', 'https:'].includes(target.protocol)) {
        throw new Error('Invalid protocol');
      }
    } catch {
      toast({ title: 'Error', description: 'Target URL must be a valid http(s) URL', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await adminApi.put(`/redirects/${editing}`, form);
        toast({ title: 'Success', description: 'Redirect updated' });
      } else {
        await adminApi.post('/redirects', form);
        toast({ title: 'Success', description: 'Redirect created' });
      }
      resetForm();
      loadRedirects();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to save redirect';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this redirect?')) return;
    try {
      await adminApi.delete(`/redirects/${id}`);
      toast({ title: 'Deleted', description: 'Redirect removed' });
      loadRedirects();
    } catch {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const handleEdit = (r) => {
    setForm({ slug: r.slug, target_url: r.target_url, is_active: r.is_active });
    setEditing(r.id);
  };

  const copyUrl = (slug) => {
    const url = `${window.location.origin}/${slug}`;
    navigator.clipboard.writeText(url);
    toast({ title: 'Copied', description: url });
  };

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="h-8 w-8 animate-spin text-red-600" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">URL Redirects</h1>
          <p className="text-gray-500 text-sm mt-1">Create short URLs that redirect to external websites. Example: <code className="bg-gray-100 px-1 rounded">{BACKEND_URL}/map</code> redirects to Google Maps</p>
        </div>
      </div>

      {/* Add/Edit Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Link2 className="h-5 w-5" />{editing ? 'Edit Redirect' : 'Add New Redirect'}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Slug *</Label>
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-400">/</span>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  placeholder="map"
                  data-testid="redirect-slug-input"
                  disabled={!!editing}
                />
              </div>
            </div>
            <div>
              <Label>Target URL *</Label>
              <Input
                value={form.target_url}
                onChange={(e) => setForm({ ...form, target_url: e.target.value })}
                placeholder="https://maps.google.com/..."
                data-testid="redirect-target-input"
              />
            </div>
            <div className="flex items-end gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Active</span>
              </label>
              <Button className="bg-red-600 hover:bg-red-700 ml-auto" onClick={handleSave} disabled={saving} data-testid="save-redirect-btn">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                {editing ? 'Update' : 'Add'}
              </Button>
              {editing && (
                <Button variant="outline" onClick={resetForm}>Cancel</Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardContent className="pt-6">
          {redirects.length === 0 ? (
            <p className="text-center text-gray-400 py-8">No redirects created yet</p>
          ) : (
            <div className="space-y-3">
              {redirects.map((r) => (
                <div key={r.id} className={`flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-lg border gap-2 ${r.is_active ? 'bg-white' : 'bg-gray-50 opacity-60'}`} data-testid={`redirect-row-${r.slug}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <code className="text-sm font-semibold text-red-600">/{r.slug}</code>
                      <span className="text-gray-400 hidden sm:inline">→</span>
                      <a href={r.target_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate max-w-[200px] sm:max-w-xs">
                        {r.target_url}
                      </a>
                      {!r.is_active && <span className="text-xs bg-gray-200 text-gray-500 px-2 py-0.5 rounded">Inactive</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 sm:ml-4 self-end sm:self-auto">
                    <Button variant="ghost" size="sm" onClick={() => copyUrl(r.slug)} title="Copy redirect URL"><Copy className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(r)}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(r.id)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminRedirects;
